import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LoaderCircle, ArrowLeft } from "lucide-react";
import { Logo } from "../components/landing/Logo";
import { useAuth, formatApiError } from "../contexts/AuthContext";

export default function LoginPage() {
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      if (mode === "login") await login(email, password);
      else await register(name, email, password);
      navigate("/app");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="login-page" className="grid-bg relative flex min-h-screen items-center justify-center overflow-hidden bg-[var(--bg-base)] px-4 text-[var(--text-primary)]">
      <div className="noise-overlay" aria-hidden="true" />
      <div aria-hidden="true" className="absolute -left-40 top-20 h-[420px] w-[420px] rounded-full bg-[#7B61FF]/20 blur-[140px]" />
      <div aria-hidden="true" className="absolute -right-32 bottom-10 h-[380px] w-[380px] rounded-full bg-[#00D4FF]/15 blur-[140px]" />

      <div className="relative z-10 w-full max-w-md">
        <Link to="/" data-testid="login-back" className="mb-6 inline-flex items-center gap-2 text-sm text-slate-400 transition-colors hover:text-white">
          <ArrowLeft className="h-4 w-4" /> Back to FUNZ
        </Link>

        <div className="glass rounded-[2rem] p-8 sm:p-10">
          <div className="flex items-center gap-3">
            <Logo className="h-11 w-11" />
            <div>
              <p className="font-display text-xl font-extrabold text-white">
                {mode === "login" ? "Welcome back" : "Join FUNZ"}
              </p>
              <p className="font-jbmono text-[10px] uppercase tracking-[0.25em] text-[#06B6D4]">
                {mode === "login" ? "Log in to your campus OS" : "Create your free account"}
              </p>
            </div>
          </div>

          <div className="mt-7 grid grid-cols-2 gap-1.5 rounded-full border border-white/10 bg-[#09090B]/80 p-1.5">
            {["login", "signup"].map((m) => (
              <button
                key={m}
                data-testid={`auth-tab-${m}`}
                onClick={() => {
                  setMode(m);
                  setError("");
                }}
                className={`rounded-full py-2.5 font-display text-xs font-bold uppercase tracking-wider transition-colors ${
                  mode === m ? "bg-gradient-to-r from-[#00D4FF] via-[#7B61FF] to-[#00FFD1] text-white" : "text-slate-400 hover:text-white"
                }`}
              >
                {m === "login" ? "Log In" : "Sign Up"}
              </button>
            ))}
          </div>

          <form onSubmit={submit} className="mt-6 space-y-4" data-testid="auth-form">
            {mode === "signup" && (
              <input
                data-testid="auth-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                className="h-13 w-full rounded-2xl border border-white/10 bg-[#09090B]/80 px-5 py-4 text-sm text-white placeholder:text-slate-500 outline-none transition-colors focus:border-[#7C3AED]/60"
              />
            )}
            <input
              data-testid="auth-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@college.edu"
              className="w-full rounded-2xl border border-white/10 bg-[#09090B]/80 px-5 py-4 text-sm text-white placeholder:text-slate-500 outline-none transition-colors focus:border-[#7C3AED]/60"
            />
            <input
              data-testid="auth-password"
              type="password"
              required
              minLength={6}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password (6+ characters)"
              className="w-full rounded-2xl border border-white/10 bg-[#09090B]/80 px-5 py-4 text-sm text-white placeholder:text-slate-500 outline-none transition-colors focus:border-[#7C3AED]/60"
            />

            {error && (
              <p data-testid="auth-error" className="rounded-xl border border-[#F97316]/30 bg-[#F97316]/10 px-4 py-3 text-sm text-[#F97316]">
                {error}
              </p>
            )}

            {mode === "login" && (
              <div className="text-right">
                <Link to="/forgot" data-testid="forgot-link" className="text-xs text-[#06B6D4] hover:underline">
                  Forgot password?
                </Link>
              </div>
            )}

            <button
              data-testid="auth-submit"
              type="submit"
              disabled={busy}
              className="btn-primary flex w-full items-center justify-center gap-2 rounded-full py-4 font-display text-sm font-bold uppercase tracking-wider text-white disabled:opacity-60"
            >
              {busy ? <LoaderCircle className="h-4 w-4 animate-spin" /> : mode === "login" ? "Log In" : "Create Account"}
            </button>
          </form>

          <p className="mt-6 text-center text-xs text-slate-500">
            One account unlocks every Bite — food, learning, jobs, shopping, travel.
          </p>
        </div>
      </div>
    </div>
  );
}
